import { useEffect, useRef, useState } from 'react';
import type { LiveActivityRow } from '@brunel/shared';
import {
  arrivingLiveActivityKeys,
  liveActivityRowSignature,
  liveActivityTimeLabel,
  shouldHighlightLiveActivityArrival,
} from '../lib/activity-cues';

interface Props {
  readonly rows: readonly LiveActivityRow[];
  readonly attentionCount: number;
  readonly onOpenAttention: () => void;
  /** When set, overrides the prefers-reduced-motion query. */
  readonly reducedMotion?: boolean;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(media.matches);
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, []);
  return reduced;
}

function ActivityRefs({ row }: {
  readonly row: LiveActivityRow;
}) {
  const refs = row.refs;
  const time = liveActivityTimeLabel(row.since);
  return (
    <span className="live-activity-refs" aria-label="Canonical references">
      {time !== null && <time dateTime={time.dateTime}>{time.label}</time>}
      {refs.taskId && <code className="live-activity-task" data-task={refs.taskId} title={refs.taskId}>task:{refs.taskId}</code>}
      {refs.pullRequest && <code>PR {refs.pullRequest.repository}#{refs.pullRequest.number}</code>}
      {refs.commandSeq !== undefined && <code>Command #{refs.commandSeq}</code>}
    </span>
  );
}

/** Ephemeral current-state presentation; never part of the durable chat timeline. */
export function LiveActivityLane({
  rows, attentionCount, onOpenAttention, reducedMotion,
}: Props) {
  const detectedReducedMotion = usePrefersReducedMotion();
  const reduceMotion = reducedMotion ?? detectedReducedMotion;
  const previousRows = useRef<readonly LiveActivityRow[] | null>(null);
  const [arrivedKeys, setArrivedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const signature = rows.map(liveActivityRowSignature).join('\n');
  useEffect(() => {
    setArrivedKeys(arrivingLiveActivityKeys(previousRows.current, rows));
    previousRows.current = rows;
  }, [signature, rows]);

  return (
    <section className="live-activity-lane" aria-label="Live Activity" data-testid="live-activity-lane">
      <header className="row spread wrap">
        <h3>Live Activity</h3>
        <small className="muted">Current state · presentation only</small>
      </header>
      <div className="live-activity-lines" role="list">
        {rows.map((row) => {
          const highlight = shouldHighlightLiveActivityArrival(arrivedKeys.has(row.actorKey), reduceMotion);
          return (
            <div
              className={highlight ? 'live-activity-line live-activity-arrive' : 'live-activity-line'}
              role="listitem"
              key={row.actorKey}
              data-actor-key={row.actorKey}
              data-worker={row.actorKind === 'worker' ? row.actorLabel : undefined}
              data-arrived={highlight ? 'true' : undefined}
            >
              <strong>{row.actorLabel}</strong>
              <span className={`live-activity-state state-${row.state.toLowerCase()}`}>{row.state}</span>
              <span className="live-activity-action">{row.action}</span>
              <ActivityRefs row={row} />
            </div>
          );
        })}
      </div>
      <nav className="command-context-actions" aria-label="Command Center records">
        <button type="button" onClick={onOpenAttention}>
          Management Attention{attentionCount > 0 && <span className="count">{attentionCount}</span>}
        </button>
      </nav>
    </section>
  );
}
